import type {ReactElement} from 'react';
import React from 'react';
import {getCurrentPageBackground} from '@Components/poster-editor/poster-editor-reducer';
import {BaseGammaOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-gamma-option';
import {GammaColorType} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-gamma-option/base-gamma-option.types';
import {useAppSelector} from '@/hooks';
import {BackgroundTypeName} from "@PosterWhiteboard/page/background/background.types";

export function ImageBgGammaOption(): ReactElement<any> | null {
  const currentPageBackground = useAppSelector(getCurrentPageBackground);
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();

  if (!currentPageBackground) {
    return null;
  }

  if (currentPageBackground.details.type !== BackgroundTypeName.IMAGE) {
    return null;
  }

  const imageDetails = currentPageBackground.details;

  const onToggleGamma = (): void => {
    void currentPage?.background.updateFromObject({
      details: {
        effects: {
          gamma: {
            isEnabled: !imageDetails.effects.gamma.isEnabled,
          },
        },
      },
    });
  };

  const onUpdateGreenGammaValue = (green: number, undoable = true): void => {
    void currentPage?.background.updateFromObject(
      {
        details: {
          effects: {
            gamma: {
              green,
            },
          },
        },
      },
      {undoable}
    );
  };

  const onUpdateRedGammaValue = (red: number, undoable = true): void => {
    void currentPage?.background.updateFromObject(
      {
        details: {
          effects: {
            gamma: {
              red,
            },
          },
        },
      },
      {undoable}
    );
  };

  const onUpdateBlueGammaValue = (blue: number, undoable = true): void => {
    void currentPage?.background.updateFromObject(
      {
        details: {
          effects: {
            gamma: {
              blue,
            },
          },
        },
      },
      {undoable}
    );
  };

  const onUpdateColor = (color: GammaColorType, value: number, undoable = true): void => {
    switch (color) {
      case GammaColorType.GREEN:
        return onUpdateGreenGammaValue(value, undoable);
      case GammaColorType.RED:
        return onUpdateRedGammaValue(value, undoable);
      case GammaColorType.BLUE:
        return onUpdateBlueGammaValue(value, undoable);
      default:
        throw new Error('unhandled color for gamma in image background gamma options');
    }
  };

  return <BaseGammaOption gamma={imageDetails.effects.gamma} updateColor={onUpdateColor} onToggleGamma={onToggleGamma} />;
}
